import React, { useEffect, useState } from 'react';
import kkpService from '../../services/kkpService';
import { canEditCreaPubFichiers, venteCanalLabel, ventePeriodeLabel } from '../../utils/kkpBatValide';
import { notify } from '../common/AppToast';
import { AppButton } from '../ui';
import KkpCreaPubFichiersModal from './KkpCreaPubFichiersModal';

const pickPubFile = (vente) => {
  const crea = vente?.crea_pub_pieces_jointes ?? [];
  const brief = vente?.brief_pieces_jointes ?? [];
  const creaImage = crea.find((file) => file.is_image);
  if (creaImage) return { ...creaImage, source: 'crea' };
  const briefImage = brief.find((file) => file.is_image);
  if (briefImage) return { ...briefImage, source: 'brief' };
  if (crea[0]) return { ...crea[0], source: 'crea' };
  return null;
};

const KkpPubImageButton = ({ vente, onCreaPubChange }) => {
  const [opening, setOpening] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [piecesOverride, setPiecesOverride] = useState(null);
  const canEdit = canEditCreaPubFichiers();

  useEffect(() => {
    setPiecesOverride(null);
  }, [vente?.id, vente?.crea_pub_pieces_jointes]);

  const displayVente = piecesOverride
    ? { ...vente, crea_pub_pieces_jointes: piecesOverride }
    : vente;
  const file = pickPubFile(displayVente);

  if (!vente?.id) return null;

  const modalVente = {
    ...displayVente,
    client_label: displayVente.client_label || displayVente.client || '—',
    canal_label: venteCanalLabel(displayVente),
    periode_label: ventePeriodeLabel(displayVente),
  };

  const handleOpen = async () => {
    if (canEdit) {
      setShowModal(true);
      return;
    }

    if (!file) {
      notify.warning('Aucune image de pub pour cette ligne. Elle vient de la créa déposée dans Suivi créa pub, ou d’une image jointe au brief.');
      return;
    }

    const popup = window.open('about:blank', '_blank');
    setOpening(true);
    try {
      const blob = file.source === 'brief'
        ? await kkpService.fetchBriefFichierBlob(vente.id, file.id, { inline: true })
        : await kkpService.fetchCreaPubFichierBlob(vente.id, file.id, { inline: true });
      const url = URL.createObjectURL(blob);
      if (popup) {
        popup.location = url;
      } else {
        window.open(url, '_blank', 'noopener,noreferrer');
      }
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      popup?.close();
      notify.error(err.message || 'Impossible d’ouvrir l’image de la pub');
    } finally {
      setOpening(false);
    }
  };

  const handlePiecesChange = (pieces) => {
    setPiecesOverride(pieces);
    onCreaPubChange?.(vente.id, pieces);
  };

  const hasFile = Boolean(file);
  const title = canEdit
    ? (hasFile ? 'Voir / modifier les créations' : 'Ajouter une création')
    : (hasFile ? 'Ouvrir l’image de la pub' : 'Aucune image de pub (créa ou brief)');

  return (
    <>
      <AppButton
        type="button"
        size="sm"
        variant={hasFile ? 'outline-success' : 'outline-secondary'}
        className={hasFile ? '' : 'opacity-50'}
        title={title}
        aria-label={title}
        onClick={handleOpen}
        loading={opening}
        icon={hasFile ? 'mdi:image-outline' : 'mdi:image-off-outline'}
      />
      {canEdit && (
        <KkpCreaPubFichiersModal
          show={showModal}
          vente={modalVente}
          onClose={() => setShowModal(false)}
          onError={(message) => notify.error(message || 'Erreur fichiers créa')}
          onChange={handlePiecesChange}
        />
      )}
    </>
  );
};

export default KkpPubImageButton;
